import type {
  CategoryScore,
  Category,
  CollectResult,
  Finding,
  EvidenceTier,
  Flag,
  Report,
  ScoreResult,
  Severity,
  VisitorIdentity,
} from '@fraudeai/schema';
import { CATEGORIES, SEVERITIES } from '@fraudeai/schema';
import type { Rule } from './types.js';
import { ALL_RULES } from './rules/index.js';

/**
 * Движок скоринга.
 *
 * Осознанные решения, заложенные в расчёт:
 *
 *  1. Скор идёт сверху вниз. Стартуем со 100 и вычитаем за найденное, а не
 *     набираем баллы за «хорошее поведение». Отсутствие улик — не заслуга.
 *
 *  2. Вес умножается на уверенность правила. Правило, которое честно
 *     сомневается, и штрафует меньше.
 *
 *  3. Низкое покрытие ограничивает скор сверху. Если половина модулей не
 *     отработала, мы не имеем права написать «чисто» — мы просто не смотрели.
 *     Это ровно тот случай, когда молчание чекера выгодно нарушителю.
 */

export interface EvaluateOptions {
  rules?: Rule[];
  identity?: VisitorIdentity;
  /**
   * Проверялся ли сетевой слой — отпечаток TLS, отпечаток HTTP/2, сверка
   * заголовков запроса с JavaScript. На клиенте почти всегда `false`.
   */
  networkLayerChecked?: boolean;
}

/**
 * Потолок оценки, когда сетевой слой не проверялся.
 *
 * Найдено на живом прогоне: антидетект-браузер не смог отправить измерения на
 * сервер, все сетевые правила не отработали, и он получил ровно ту же оценку,
 * что и обычный Chrome. Формально верно — в браузере зацепиться было не за
 * что, — но по существу это ввод в заблуждение: непроверенное выглядело как
 * чистое. Именно сетевой слой и ловит антидетекты, поэтому без него оценка
 * выше среднего невозможна в принципе.
 */
const NO_NETWORK_LAYER_CAP = 60;
const UNCORROBORATED_EVIDENCE_FACTOR = 0.35;
const UNCORROBORATED_EVIDENCE_CAP = 15;

const ATTRIBUTION_FLAGS = new Set<Flag>([
  'ANTIDETECT',
  'AUTOMATION',
  'BOT',
  'SPOOFING',
  'VIRTUAL_MACHINE',
]);

const VERDICT_THRESHOLDS: [number, ScoreResult['verdict']][] = [
  [85, 'clean'],
  [65, 'suspicious'],
  [35, 'high_risk'],
  [0, 'fraudulent'],
];

function verdictFor(
  score: number,
  authenticity: number,
  flags: Set<Flag>,
): ScoreResult['verdict'] {
  // Отдельный случай: к браузеру претензий нет, а к сети есть. Без него
  // настоящий Chrome за протекающим VPN получал подпись «окружение
  // подделано» — утверждение о браузере, выведенное из свойств канала.
  if (authenticity >= 85 && score < 85) {
    if (flags.has('PROXY_VPN') || flags.has('HOSTING') || flags.has('TOR')) {
      return 'genuine_browser_with_vpn';
    }
    return 'network_risk';
  }

  for (const [threshold, verdict] of VERDICT_THRESHOLDS) {
    if (score >= threshold) return verdict;
  }
  return 'fraudulent';
}

function clamp(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function roundRatio(value: number): number {
  return Math.round(value * 100) / 100;
}

// verdictConfidence — насколько можно доверять самому вердикту, а не насколько
// он суров. Зеркалит services/api-go/internal/rules/engine.go.
//
// Обвинение хрупко ложным срабатыванием и тем надёжнее, чем прямее вызвавшая
// его улика. «Чисто» хрупко пропуском и тем надёжнее, чем полнее проверка.
function isAccusation(verdict: ScoreResult['verdict']): boolean {
  return verdict === 'suspicious' || verdict === 'high_risk' || verdict === 'fraudulent';
}

function clampConfidence(value: number): number {
  return Math.max(0.3, Math.min(1, value));
}

// accusationConfidence — прочность улики, обвинившей браузер. Смотрим только на
// браузерные атрибутивные находки: сеть подлинность не снижает.
function accusationConfidence(
  findings: Finding[],
  corroboration: Map<Flag, Set<string>>,
): number {
  let best = 0;
  for (const finding of findings) {
    if (finding.category === 'network' || finding.category === 'privacy') continue;
    if (!finding.flags.some((flag) => ATTRIBUTION_FLAGS.has(flag))) continue;
    const { tier } = normalizedEvidence(finding);
    if (tier === 'direct') {
      // Прямая улика ошибкой почти не бывает; ограничена лишь тем, насколько
      // уверенно измерена.
      best = Math.max(best, 0.85 + 0.1 * finding.confidence);
    } else if (tier === 'corroborating') {
      // Косвенная улика надёжна ровно настолько, насколько независимы
      // подтверждающие её группы: одна может быть совпадением, две — вряд ли.
      let groups = 0;
      for (const flag of finding.flags) {
        groups = Math.max(groups, corroboration.get(flag)?.size ?? 0);
      }
      const w = groups >= 3 ? 0.85 : groups === 2 ? 0.72 : 0.5;
      best = Math.max(best, w);
    }
  }
  return best === 0 ? 0.5 : roundRatio(clampConfidence(best));
}

// clearanceConfidence — насколько полно проверили, прежде чем сказать «чисто».
function clearanceConfidence(
  coverage: number,
  networkCoverage: number,
  limitedBy: ScoreResult['limitedBy'],
): number {
  let c = coverage;
  if (limitedBy === 'network-layer') c *= 0.55;
  else if (limitedBy === 'coverage') c *= 0.9;
  c *= 0.75 + 0.25 * networkCoverage;
  return roundRatio(clampConfidence(c));
}

function verdictConfidence(
  verdict: ScoreResult['verdict'],
  coverage: number,
  networkCoverage: number,
  limitedBy: ScoreResult['limitedBy'],
  findings: Finding[],
  corroboration: Map<Flag, Set<string>>,
): number {
  return isAccusation(verdict)
    ? accusationConfidence(findings, corroboration)
    : clearanceConfidence(coverage, networkCoverage, limitedBy);
}

function clientNetworkCoverage(
  result: CollectResult,
  options: Pick<EvaluateOptions, 'networkLayerChecked'>,
): number {
  let total = 0;
  let done = 0;

  total += 1;
  if (options.networkLayerChecked) done += 1;

  const probes = result.signals.probes;
  if (probes?.requested && probes.attempted.length > 0) {
    const failed = new Set(probes.failed);
    for (const kind of new Set(probes.attempted)) {
      total += 1;
      if (!failed.has(kind)) done += 1;
    }
  }

  return total === 0 ? 0 : roundRatio(done / total);
}

export function runRules(result: CollectResult, rules: Rule[] = ALL_RULES): Finding[] {
  const findings: Finding[] = [];
  const available = new Set(
    result.modules.filter((m) => m.ok).map((m) => m.id as string),
  );

  for (const rule of rules) {
    // Правило без своих данных не «не сработало», а «не проверялось».
    // Разница существенная, поэтому такие правила просто пропускаем.
    if (!rule.meta.requires.every((id) => available.has(id))) continue;

    let hit;
    try {
      hit = rule.evaluate(result.signals);
    } catch {
      // Ошибка в правиле не должна ронять весь отчёт.
      continue;
    }
    if (!hit) continue;

    const confidence = Math.max(0, Math.min(1, hit.confidence ?? 1));

    const flags = hit.flags ?? rule.meta.flags;
    const hasAttribution = flags.some((flag) => ATTRIBUTION_FLAGS.has(flag));
    findings.push({
      ruleId: rule.meta.id,
      title: rule.meta.title,
      severity: hit.severity ?? rule.meta.severity,
      category: rule.meta.category,
      flags,
      weight: rule.meta.weight,
      confidence,
      explanation: hit.explanation,
      evidence: hit.evidence,
      evidenceTier: hasAttribution
        ? (rule.meta.evidenceTier ?? 'corroborating')
        : 'observation',
      ...(hasAttribution
        ? { evidenceGroup: rule.meta.evidenceGroup ?? rule.meta.id }
        : {}),
      ...(rule.meta.stage === 'shadow' ? { shadow: true } : {}),
    });
  }

  const severityOrder = new Map(SEVERITIES.map((s, i) => [s, i]));
  findings.sort((a, b) => {
    const bySeverity =
      (severityOrder.get(a.severity) ?? 99) - (severityOrder.get(b.severity) ?? 99);
    if (bySeverity !== 0) return bySeverity;
    return b.weight * b.confidence - a.weight * a.confidence;
  });

  return findings;
}

function normalizedEvidence(finding: Finding): { tier: EvidenceTier; group: string } {
  return {
    tier: finding.evidenceTier ?? 'corroborating',
    group: finding.evidenceGroup ?? finding.ruleId,
  };
}

function corroborationGroups(findings: Finding[]): Map<Flag, Set<string>> {
  const groups = new Map<Flag, Set<string>>();
  for (const finding of findings) {
    if (finding.confidence < 0.5) continue;
    const { tier, group } = normalizedEvidence(finding);
    if (tier !== 'direct' && tier !== 'corroborating') continue;
    for (const flag of finding.flags) {
      if (!ATTRIBUTION_FLAGS.has(flag)) continue;
      const values = groups.get(flag) ?? new Set<string>();
      values.add(group);
      groups.set(flag, values);
    }
  }
  return groups;
}

function findingIsCorroborated(finding: Finding, groups: Map<Flag, Set<string>>): boolean {
  return finding.flags.some(
    (flag) => ATTRIBUTION_FLAGS.has(flag) && (groups.get(flag)?.size ?? 0) >= 2,
  );
}

function attributionPenaltyFactor(
  finding: Finding,
  groups: Map<Flag, Set<string>>,
): number {
  if (!finding.flags.some((flag) => ATTRIBUTION_FLAGS.has(flag))) return 1;
  const { tier } = normalizedEvidence(finding);
  if (tier === 'direct') return 1;
  if (tier === 'corroborating') {
    return findingIsCorroborated(finding, groups) ? 1 : UNCORROBORATED_EVIDENCE_FACTOR;
  }
  return 0;
}

function attributionPenalty(finding: Finding, groups: Map<Flag, Set<string>>): number {
  if (!finding.flags.some((flag) => ATTRIBUTION_FLAGS.has(flag))) {
    return finding.weight * finding.confidence;
  }
  const applied =
    finding.weight * finding.confidence * attributionPenaltyFactor(finding, groups);
  const { tier } = normalizedEvidence(finding);
  if (
    tier === 'corroborating' &&
    !findingIsCorroborated(finding, groups)
  ) {
    return Math.min(applied, UNCORROBORATED_EVIDENCE_CAP);
  }
  return applied;
}

function shouldPublishFlag(
  finding: Finding,
  flag: Flag,
  groups: Map<Flag, Set<string>>,
): boolean {
  if (finding.confidence < 0.5) return false;
  if (!ATTRIBUTION_FLAGS.has(flag)) return true;
  const { tier } = normalizedEvidence(finding);
  if (tier === 'direct') return finding.confidence >= 0.75;
  if (tier === 'corroborating') return (groups.get(flag)?.size ?? 0) >= 2;
  return false;
}

export function score(
  result: CollectResult,
  findings: Finding[],
  options: Pick<EvaluateOptions, 'networkLayerChecked'> = {},
): ScoreResult {
  // Теневые находки в счёт не идут: непроверенное правило не должно двигать
  // вердикт. Зеркалит WithoutShadow в engine.go.
  findings = findings.filter((finding) => !finding.shadow);

  const totalModules = result.modules.length;
  const okModules = result.modules.filter((m) => m.ok).length;
  const coverage = totalModules === 0 ? 0 : okModules / totalModules;

  const categoryPenalty = new Map<Category, number>();
  const categoryCount = new Map<Category, number>();
  const bySeverity = Object.fromEntries(SEVERITIES.map((s) => [s, 0])) as Record<Severity, number>;
  const flags = new Set<Flag>();

  // Штрафы копятся раздельно: сеть отвечает на вопрос «откуда пришли»,
  // всё остальное — на вопрос «кто пришёл». Общее число смешивало их, и
  // честный браузер за протекающим VPN выглядел хуже антидетект-сборки
  // через аккуратный прокси.
  let networkPenalty = 0;
  let browserPenalty = 0;
  const browserGroupPenalty = new Map<string, number>();
  const categoryGroupPenalty = new Map<string, { category: Category; penalty: number }>();
  const corroboration = corroborationGroups(findings);

  for (const finding of findings) {
    const applied = finding.weight * finding.confidence;
    const browserApplied = attributionPenalty(finding, corroboration);
    if (finding.category === 'network') networkPenalty += applied;
    // Privacy-находки видны в отчёте, но не уменьшают подлинность браузера:
    // это осознанная защита, а не подмена окружения.
    else if (finding.category !== 'privacy') {
      if (finding.flags.some((flag) => ATTRIBUTION_FLAGS.has(flag))) {
        const { group } = normalizedEvidence(finding);
        browserGroupPenalty.set(
          group,
          Math.max(browserGroupPenalty.get(group) ?? 0, browserApplied),
        );
        const categoryKey = `${finding.category}\0${group}`;
        const previous = categoryGroupPenalty.get(categoryKey)?.penalty ?? 0;
        categoryGroupPenalty.set(categoryKey, {
          category: finding.category,
          penalty: Math.max(previous, browserApplied),
        });
      } else {
        browserPenalty += browserApplied;
        categoryPenalty.set(
          finding.category,
          (categoryPenalty.get(finding.category) ?? 0) + browserApplied,
        );
      }
    }

    if (finding.category === 'network' || finding.category === 'privacy') {
      categoryPenalty.set(
        finding.category,
        (categoryPenalty.get(finding.category) ?? 0) + applied,
      );
    }
    categoryCount.set(finding.category, (categoryCount.get(finding.category) ?? 0) + 1);
    bySeverity[finding.severity] += 1;

    // Флаг ставится только при разумной уверенности: он попадает в шапку
    // отчёта, и вешать ярлык на догадку нечестно.
    for (const flag of finding.flags) {
      if (shouldPublishFlag(finding, flag, corroboration)) flags.add(flag);
    }
  }
  for (const applied of browserGroupPenalty.values()) browserPenalty += applied;
  for (const { category, penalty } of categoryGroupPenalty.values()) {
    categoryPenalty.set(category, (categoryPenalty.get(category) ?? 0) + penalty);
  }

  // Потолок по покрытию: чем меньше проверок отработало, тем меньше у нас
  // оснований утверждать, что всё в порядке.
  const coverageCap = coverage >= 0.9 ? 100 : clamp(100 - (0.9 - coverage) * 100);
  const layerCap = options.networkLayerChecked ? 100 : NO_NETWORK_LAYER_CAP;
  const authenticity = clamp(100 - browserPenalty);
  const networkTrust = clamp(100 - networkPenalty);
  const networkCoverage = clientNetworkCoverage(result, options);
  // Недоверенная сеть ограничивает оценку сверху, но не обнуляет её:
  // даже в худшем случае остаётся сорок баллов. Приговор выносится по
  // подлинности браузера, и снизить его до приговора может только сам
  // браузер.
  const networkCap = 40 + Math.round(0.6 * networkTrust);
  let trustScore = Math.min(authenticity, networkCap);

  // Причина ограничения важнее самого числа: «нашли расхождения» и
  // «не смогли проверить» дают похожие баллы, но означают разное.
  let limitedBy: ScoreResult['limitedBy'];
  if (trustScore > coverageCap) {
    trustScore = coverageCap;
    limitedBy = 'coverage';
  }
  if (trustScore > layerCap) {
    trustScore = layerCap;
    limitedBy = 'network-layer';
  }

  const byCategory: CategoryScore[] = CATEGORIES.filter((c) => categoryCount.has(c)).map(
    (category) => ({
      category,
      score: clamp(100 - (categoryPenalty.get(category) ?? 0)),
      findings: categoryCount.get(category) ?? 0,
    }),
  );

  const verdict = verdictFor(trustScore, authenticity, flags);

  return {
    trustScore,
    authenticity,
    networkTrust,
    networkCoverage,
    verdict,
    flags: [...flags],
    byCategory,
    bySeverity,
    coverage: roundRatio(coverage),
    ...(limitedBy ? { limitedBy } : {}),
    confidence: verdictConfidence(
      verdict,
      coverage,
      networkCoverage,
      limitedBy,
      findings,
      corroboration,
    ),
  };
}

/** Собирает готовый отчёт: сигналы, находки, скор и идентификатор посетителя. */
export function buildReport(result: CollectResult, options: EvaluateOptions = {}): Report {
  const findings = runRules(result, options.rules ?? ALL_RULES);
  return {
    ...result,
    identity: options.identity ?? { visitorId: 'unknown', confidence: 0, components: [] },
    findings,
    score: score(result, findings, { networkLayerChecked: options.networkLayerChecked }),
  };
}
